import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import colors from '../../styles/colors';

const SUPPORT_EMAIL = 'jeff@visibilitylabs.ai';

const styles = {
  wrapper: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: colors.bg.offWhite,
  },
  header: {
    background: colors.bg.white,
    padding: '16px 0',
    borderBottom: `1px solid ${colors.border.lightNavy}`,
  },
  headerInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImg: { height: '34px', width: 'auto' },
  main: { flex: 1, padding: '48px 0' },
  mainInner: { maxWidth: '980px', margin: '0 auto', padding: '0 24px' },
  title: {
    fontSize: '28px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: colors.text.muted,
    marginBottom: '18px',
    lineHeight: '1.7',
  },
  instructionsTitle: {
    fontSize: '15px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  instructionsList: {
    margin: '0 0 18px',
    paddingLeft: '20px',
    fontSize: '14px',
    color: colors.text.muted,
    lineHeight: '1.8',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    borderRadius: '999px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    fontSize: '13px',
    fontWeight: '800',
    color: colors.text.darkNavy,
  },
  card: {
    background: colors.bg.white,
    border: `1px solid ${colors.border.lightNavy}`,
    borderRadius: '12px',
    padding: '18px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  commentRow: {
    display: 'grid',
    gridTemplateColumns: '40px 1fr auto',
    gap: '14px',
    padding: '18px 0',
    borderBottom: `1px solid ${colors.border.light}`,
    alignItems: 'start',
  },
  indexCol: {
    paddingTop: '3px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  index: {
    fontSize: '12px',
    color: colors.text.muted,
    fontWeight: '700',
  },
  redditIcon: {
    width: '22px',
    height: '22px',
    opacity: 0.55,
  },
  contentCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  postTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: colors.text.darkNavy,
    lineHeight: '1.5',
  },
  redditLink: {
    fontSize: '12px',
    color: colors.text.accentBlue || '#3b82f6',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: '600',
    wordBreak: 'break-all',
  },
  commentTextarea: {
    width: '100%',
    minHeight: '130px',
    resize: 'vertical',
    borderRadius: '8px',
    border: `1px solid ${colors.border.light}`,
    padding: '10px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: colors.text.darkNavy,
    lineHeight: '1.65',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#f8f9fb',
    transition: 'border 0.15s, background 0.15s',
  },
  commentTextareaRejected: {
    border: '1px solid rgba(220, 38, 38, 0.4)',
    background: 'rgba(220, 38, 38, 0.03)',
  },
  commentTextareaEdited: {
    border: `1px solid rgba(124,58,237,0.35)`,
    background: 'rgba(124,58,237,0.03)',
  },
  rejectionFeedbackWrap: {
    marginTop: '2px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: 'rgba(220,38,38,0.04)',
    border: '1px solid rgba(220,38,38,0.18)',
  },
  rejectionFeedbackLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#b91c1c',
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  rejectionFeedbackTextarea: {
    width: '100%',
    minHeight: '64px',
    resize: 'vertical',
    borderRadius: '6px',
    border: '1px solid rgba(220,38,38,0.25)',
    padding: '8px 10px',
    fontSize: '13px',
    fontFamily: 'inherit',
    color: colors.text.darkNavy,
    lineHeight: '1.55',
    outline: 'none',
    boxSizing: 'border-box',
    background: colors.bg.white,
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '2px',
  },
  editedTag: {
    display: 'inline-block',
    fontSize: '12px',
    fontWeight: '800',
    color: colors.text.purple || '#7c3aed',
    background: 'rgba(124,58,237,0.08)',
    borderRadius: '999px',
    padding: '2px 10px',
  },
  rejectedTag: {
    display: 'inline-block',
    fontSize: '12px',
    fontWeight: '800',
    color: '#b91c1c',
    background: 'rgba(185,28,28,0.07)',
    borderRadius: '999px',
    padding: '2px 10px',
  },
  actionsCol: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    paddingTop: '2px',
  },
  rejectBtn: {
    borderRadius: '8px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#b91c1c',
    whiteSpace: 'nowrap',
    transition: 'border 0.15s, background 0.15s',
  },
  rejectBtnActive: {
    border: '1px solid rgba(220, 38, 38, 0.55)',
    background: 'rgba(220, 38, 38, 0.08)',
    color: '#991b1b',
  },
  rejectBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  summaryToolbar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: '8px',
    marginBottom: '4px',
    paddingTop: '20px',
  },
  feedbackSection: {
    marginTop: '16px',
  },
  feedbackLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  feedbackBox: {
    width: '100%',
    minHeight: '120px',
    resize: 'vertical',
    borderRadius: '10px',
    border: `1px solid ${colors.border.light}`,
    padding: '12px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#f8f9fb',
  },
  actions: { marginTop: '18px' },
  submitActions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '10px',
  },
  submitButton: {
    flex: '0 0 auto',
  },
  button: {
    background: colors.bg.primaryPurple,
    color: colors.text.white,
    border: 'none',
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
    transition: 'background 0.15s ease, transform 0.05s ease, box-shadow 0.15s ease',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  buttonSecondary: {
    background: colors.bg.white,
    color: colors.text.darkNavy,
    border: `1px solid ${colors.border.lightNavy}`,
    borderRadius: '10px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: '800',
    cursor: 'pointer',
    transition: 'background 0.15s ease, border-color 0.15s ease, transform 0.05s ease, box-shadow 0.15s ease',
  },
  errorBanner: {
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
    color: '#7f1d1d',
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '18px',
    fontSize: '14px',
    fontWeight: '700',
  },
  thankYouWrap: {
    background: `linear-gradient(135deg, ${colors.bg.primaryPurple} 0%, ${colors.bg.darkNavy} 100%)`,
    borderRadius: '16px',
    padding: '28px',
    marginBottom: '24px',
    color: colors.text.white,
    textAlign: 'center',
    boxShadow: '0 12px 30px rgba(23, 15, 73, 0.18)',
  },
  thankYouIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '18px',
    background: 'rgba(255,255,255,0.14)',
    border: '1px solid rgba(255,255,255,0.22)',
    margin: '0 auto 18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thankYouTitle: {
    fontSize: '28px',
    fontWeight: '800',
    marginBottom: '10px',
    letterSpacing: '-0.5px',
  },
  thankYouText: {
    maxWidth: '620px',
    margin: '0 auto',
    fontSize: '15px',
    lineHeight: '1.7',
    color: 'rgba(255,255,255,0.86)',
  },
  footer: {
    background: colors.bg.darkNavy,
    padding: '22px 0',
    textAlign: 'center',
  },
  footerText: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },

  // Confirmation modal
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 12, 40, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px',
  },
  modalCard: {
    background: colors.bg.white,
    borderRadius: '16px',
    padding: '32px 28px 24px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(15,12,40,0.22)',
    textAlign: 'center',
  },
  modalIcon: {
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    background: `rgba(124,58,237,0.1)`,
    border: `1px solid rgba(124,58,237,0.2)`,
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '800',
    color: colors.text.darkNavy,
    marginBottom: '8px',
  },
  modalBody: {
    fontSize: '14px',
    color: colors.text.muted,
    lineHeight: '1.65',
    marginBottom: '24px',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
  },
  modalCancelBtn: {
    flex: 1,
    padding: '11px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border.lightNavy}`,
    background: colors.bg.white,
    fontSize: '14px',
    fontWeight: '700',
    color: colors.text.darkNavy,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  modalConfirmBtn: {
    flex: 1,
    padding: '11px 16px',
    borderRadius: '10px',
    border: 'none',
    background: colors.bg.primaryPurple,
    fontSize: '14px',
    fontWeight: '800',
    color: colors.text.white,
    cursor: 'pointer',
    transition: 'background 0.15s ease, transform 0.05s ease, box-shadow 0.15s ease',
  },
};

function ConfirmModal({ approvedCount, rejectedCount, total, autoApproveGoingForward, onConfirm, onCancel }) {
  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(124,58,237,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
            <rect x="9" y="3" width="6" height="4" rx="1" />
            <line x1="9" y1="12" x2="15" y2="12" />
            <line x1="9" y1="16" x2="13" y2="16" />
          </svg>
        </div>
        <div style={styles.modalTitle}>
          {autoApproveGoingForward ? 'Auto-approve going forward?' : 'Submit your review?'}
        </div>
        <div style={styles.modalBody}>
          You're about to submit your review for <b>{total}</b> comment{total !== 1 ? 's' : ''}.
          <br />
          <span style={{ color: '#15803d', fontWeight: '700' }}>{approvedCount} approved</span>
          {rejectedCount > 0 && (
            <>, <span style={{ color: '#b91c1c', fontWeight: '700' }}>{rejectedCount} rejected</span></>
          )}.
          <br /><br />
          {autoApproveGoingForward ? (
            <>
              Your approved comments will be processed this month, and <b>future months will skip this approval form</b> — comments will be approved automatically.
              <br /><br />
              This cannot be undone. Are you sure?
            </>
          ) : (
            <>This cannot be undone. Are you sure?</>
          )}
        </div>
        <div style={styles.modalActions}>
          <button type="button" className="rca-modal-cancel-btn" style={styles.modalCancelBtn} onClick={onCancel}>
            Go back
          </button>
          <button type="button" className="rca-modal-confirm-btn" style={styles.modalConfirmBtn} onClick={onConfirm}>
            {autoApproveGoingForward ? 'Yes, auto-approve' : 'Yes, submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RedditCommentsApproval() {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [locked, setLocked] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAutoApprove, setPendingAutoApprove] = useState(false);

  const [clientName, setClientName] = useState('');
  const [reviewer, setReviewer] = useState('Client');
  const [commentsOriginal, setCommentsOriginal] = useState([]);
  const [comments, setComments] = useState([]);
  // Map<id, feedbackString> for per-comment rejection feedback
  const [rejectionFeedback, setRejectionFeedback] = useState(() => new Map());
  const [overallFeedback, setOverallFeedback] = useState('');
  const [rejectedIds, setRejectedIds] = useState(() => new Set());

  const detailUrl = useMemo(() => `/forms/reddit-comments-approval/${uuid}/`, [uuid]);

  useEffect(() => {
    setLoading(true);
    setError('');
    setNotFound(false);

    api
      .get(detailUrl)
      .then((res) => {
        const data = res.data || {};
        const isLocked = Boolean(data?.is_locked);
        setLocked(isLocked);
        setShowThankYou(isLocked);
        setClientName(data?.client_name || '');
        setReviewer(data?.reviewer || 'Client');
        const orig = data?.comments_original || [];
        const working = data?.comments_working || orig;
        setCommentsOriginal(orig);
        setComments(working.length ? working : orig);
        setOverallFeedback((data?.additional_comments || '').replaceAll('\\n', '\n'));
        const rejIds = new Set((data?.rejected_comment_ids || []).map((x) => Number(x)));
        setRejectedIds(rejIds);
        // Restore per-comment feedback if session was already submitted
        const feedbackMap = new Map();
        (data?.rejection_feedback || []).forEach((item) => {
          feedbackMap.set(Number(item.Id), item.feedback || '');
        });
        setRejectionFeedback(feedbackMap);
        setLoading(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.detail || 'This form link is invalid.';
        setError(msg);
        setNotFound(err?.response?.status === 404);
        setLoading(false);
      });
  }, [detailUrl]);

  const rejectedCount = useMemo(() => rejectedIds.size, [rejectedIds]);
  const approvedCount = useMemo(() => comments.length - rejectedIds.size, [comments, rejectedIds]);

  const editedCount = useMemo(() => {
    const byId = new Map(commentsOriginal.map((c) => [Number(c.Id), c.comment || '']));
    return comments.filter((c) => (c.comment || '') !== (byId.get(Number(c.Id)) ?? '')).length;
  }, [comments, commentsOriginal]);

  const updateComment = (id, text) => {
    setComments((prev) => prev.map((c) => (Number(c.Id) === id ? { ...c, comment: text } : c)));
  };

  const updateRejectionFeedback = (id, text) => {
    setRejectionFeedback((prev) => {
      const next = new Map(prev);
      next.set(id, text);
      return next;
    });
  };

  const toggleRejected = (id) => {
    if (locked) return;
    setRejectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        // Clear feedback when un-rejecting
        setRejectionFeedback((fb) => {
          const n = new Map(fb);
          n.delete(id);
          return n;
        });
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const doSubmit = () => {
    setShowConfirm(false);
    setSubmitting(true);
    setError('');

    const rejectionFeedbackPayload = Array.from(rejectedIds).map((id) => ({
      Id: id,
      feedback: (rejectionFeedback.get(id) || '').trim(),
    }));

    api
      .post(`${detailUrl}submit/`, {
        comments,
        rejected_comment_ids: Array.from(rejectedIds),
        rejection_feedback: rejectionFeedbackPayload,
        additional_comments: overallFeedback,
        auto_approve_going_forward: pendingAutoApprove,
      })
      .then(() => {
        setLocked(true);
        setShowThankYou(true);
        setSubmitting(false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.detail ||
          'Could not submit the form. Please try again or contact support.';
        setError(msg);
        setSubmitting(false);
      });
  };

  const openConfirm = (autoApproveGoingForward) => {
    if (locked || submitting) return;
    setPendingAutoApprove(autoApproveGoingForward);
    setShowConfirm(true);
  };

  const onSubmit = (e) => {
    e.preventDefault();
  };

  return (
    <div style={styles.wrapper}>
      {/* Responsive styles — injected since this page uses inline styles only */}
      <style>{`
        .rca-btn-primary:hover:not(:disabled) {
          background: rgb(101, 82, 235) !important;
          box-shadow: 0 4px 12px rgba(122, 102, 255, 0.35);
        }
        .rca-btn-primary:active:not(:disabled) {
          transform: scale(0.98);
          box-shadow: 0 2px 6px rgba(124, 58, 237, 0.25);
        }
        .rca-btn-secondary:hover:not(:disabled) {
          background: rgba(124, 58, 237, 0.06) !important;
          border-color: ${colors.bg.primaryPurple} !important;
          color: ${colors.bg.primaryPurple} !important;
        }
        .rca-btn-secondary:active:not(:disabled) {
          transform: scale(0.98);
          background: rgba(124, 58, 237, 0.1) !important;
        }
        .rca-reject-btn:hover:not(:disabled) {
          filter: brightness(0.96);
        }
        .rca-reject-btn:active:not(:disabled) {
          transform: scale(0.96);
        }
        .rca-modal-cancel-btn:hover {
          background: rgba(15, 12, 40, 0.04) !important;
        }
        .rca-modal-confirm-btn:hover {
          background: rgb(101, 82, 235) !important;
          box-shadow: 0 4px 12px rgba(122, 102, 255, 0.35);
        }
        .rca-modal-confirm-btn:active {
          transform: scale(0.98);
        }
        @media (max-width: 640px) {
          .rca-main { padding: 24px 0 !important; }
          .rca-comment-row {
            grid-template-columns: 1fr !important;
            gap: 8px !important;
          }
          .rca-index-col {
            flex-direction: row !important;
            align-items: center !important;
            gap: 8px !important;
            padding-top: 0 !important;
          }
          .rca-actions-col {
            justify-content: flex-start !important;
          }
          .rca-submit-actions {
            flex-direction: column !important;
            width: 100%;
          }
          .rca-submit-btn {
            width: 100%;
            flex: none !important;
            min-width: 0 !important;
            box-sizing: border-box;
          }
        }
        @media (max-width: 480px) {
          .rca-main { padding: 16px 0 !important; }
        }
      `}</style>

      {showConfirm && (
        <ConfirmModal
          approvedCount={approvedCount}
          rejectedCount={rejectedCount}
          total={comments.length}
          autoApproveGoingForward={pendingAutoApprove}
          onConfirm={doSubmit}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      <header style={styles.header}>
        <div style={styles.headerInner}>
          <a href="https://visibilitylabs.ai" aria-label="Go to visibilitylabs.ai">
            <img src="/visibilitylabs.svg" alt="Visibility Labs" style={styles.logoImg} />
          </a>
        </div>
      </header>

      <main style={styles.main} className="rca-main">
        <div style={styles.mainInner}>
          {loading ? (
            <div style={styles.card}>Loading form…</div>
          ) : (
            <>
              {notFound && (
                <div style={styles.errorBanner}>
                  Link not found. If you believe this is an error, email{' '}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    style={{ color: colors.text.accentBlue, fontWeight: 800 }}
                  >
                    {SUPPORT_EMAIL}
                  </a>
                  .
                </div>
              )}

              {showThankYou && !notFound && (
                <div style={styles.thankYouWrap}>
                  <div style={styles.thankYouIcon}>
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.92)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div style={styles.thankYouTitle}>Thank you!</div>
                  <div style={styles.thankYouText}>
                    Thanks for reviewing the Reddit comments for{' '}
                    {clientName || 'your company'}.
                    <br />
                    We've received your feedback and will take it from here.
                    <br />
                    Full speed ahead!
                  </div>
                </div>
              )}

              {!notFound && (
                <>
                  <h1 style={styles.title}>
                    {clientName ? `${clientName}: Reddit Comment Approval` : 'Reddit Comment Approval'}
                  </h1>
                  <div style={styles.instructionsTitle}>Instructions</div>
                  <ul style={styles.instructionsList}>
                    <li>Review the Reddit comments below. You can edit the comment text directly if needed.</li>
                    <li>Click <b>Reject</b> on any comment you don't like. You also have the option to leave a rejection reason.</li>
                    <li>
                      Add any general notes at the bottom (if any), then click <b>Approve</b> to submit your review
                      {reviewer === 'Client' && (
                        <>, or <b>Approve &amp; Auto-Approve Going Forward</b> to skip future monthly reviews</>
                      )}.
                    </li>
                  </ul>

                  {error && <div style={styles.errorBanner}>{error}</div>}

                  <form onSubmit={onSubmit} style={styles.card}>
                    {comments.map((c, idx) => {
                      const id = Number(c.Id);
                      const isRejected = rejectedIds.has(id);
                      const originalComment = commentsOriginal.find((o) => Number(o.Id) === id)?.comment || '';
                      const isEdited = (c.comment || '') !== originalComment;
                      return (
                        <div key={id} style={styles.commentRow} className="rca-comment-row">
                          <div style={styles.indexCol} className="rca-index-col">
                            <span style={styles.index}>#{idx + 1}</span>
                            <svg style={styles.redditIcon} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                              <circle cx="10" cy="10" r="10" fill="#FF4500" />
                              <path
                                d="M16.67 10a1.46 1.46 0 0 0-2.47-1 7.12 7.12 0 0 0-3.85-1.23l.65-3.07 2.13.45a1 1 0 1 0 .1-.48l-2.38-.5a.14.14 0 0 0-.16.1l-.73 3.43a7.14 7.14 0 0 0-3.89 1.23 1.46 1.46 0 1 0-1.6 2.36 2.83 2.83 0 0 0 0 .39c0 2 2.33 3.61 5.21 3.61s5.21-1.62 5.21-3.61a2.83 2.83 0 0 0 0-.39 1.46 1.46 0 0 0 .68-1.29zM7.5 11a1 1 0 1 1 1 1 1 1 0 0 1-1-1zm5.6 2.65a3.56 3.56 0 0 1-3.1.9 3.56 3.56 0 0 1-3.1-.9.18.18 0 0 1 .26-.26 3.2 3.2 0 0 0 2.84.77 3.2 3.2 0 0 0 2.84-.77.18.18 0 0 1 .26.26zm-.1-1.65a1 1 0 1 1 1-1 1 1 0 0 1-1 1z"
                                fill="#fff"
                              />
                            </svg>
                          </div>

                          <div style={styles.contentCol}>
                            <div style={styles.postTitle}>{c.post_title || '—'}</div>
                            <a
                              href={c.reddit_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.redditLink}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                <polyline points="15 3 21 3 21 9" />
                                <line x1="10" y1="14" x2="21" y2="3" />
                              </svg>
                              {c.reddit_url}
                            </a>
                            <textarea
                              style={{
                                ...styles.commentTextarea,
                                ...(isRejected ? styles.commentTextareaRejected : {}),
                                ...(isEdited && !isRejected ? styles.commentTextareaEdited : {}),
                              }}
                              value={c.comment || ''}
                              disabled={locked}
                              onChange={(e) => updateComment(id, e.target.value)}
                              rows={5}
                            />
                            {(isRejected || (isEdited && !isRejected)) && (
                              <div style={styles.rowMeta}>
                                {isRejected && (
                                  <span style={styles.rejectedTag}>Rejected</span>
                                )}
                                {isEdited && !isRejected && (
                                  <span style={styles.editedTag}>Edited</span>
                                )}
                              </div>
                            )}

                            {isRejected && (
                              <div style={styles.rejectionFeedbackWrap}>
                                <div style={styles.rejectionFeedbackLabel}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                  </svg>
                                  Why are you rejecting this? (optional)
                                </div>
                                <textarea
                                  style={styles.rejectionFeedbackTextarea}
                                  value={rejectionFeedback.get(id) || ''}
                                  disabled={locked}
                                  onChange={(e) => updateRejectionFeedback(id, e.target.value)}
                                  placeholder="Tell us what's wrong or what you'd like changed…"
                                  rows={2}
                                />
                              </div>
                            )}
                          </div>

                          <div style={styles.actionsCol} className="rca-actions-col">
                            <button
                              type="button"
                              className="rca-reject-btn"
                              title={isRejected ? 'Undo rejection' : 'Reject comment'}
                              aria-label={isRejected ? 'Undo rejection' : 'Reject comment'}
                              onClick={() => toggleRejected(id)}
                              disabled={locked}
                              style={{
                                ...styles.rejectBtn,
                                ...(isRejected ? styles.rejectBtnActive : {}),
                                ...(locked ? styles.rejectBtnDisabled : {}),
                              }}
                            >
                              {isRejected ? 'Undo' : 'Reject'}
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    <div style={styles.summaryToolbar}>
                      <div style={{ ...styles.pill, color: '#15803d', borderColor: 'rgba(21,128,61,0.25)' }}>
                        Approved: {approvedCount}/{comments.length}
                      </div>
                      <div style={{ ...styles.pill, color: rejectedCount > 0 ? '#b91c1c' : colors.text.darkNavy }}>
                        Rejected: {rejectedCount}
                      </div>
                    </div>

                    <div style={styles.feedbackSection}>
                      <label style={styles.feedbackLabel}>
                        General Feedback{' '}
                        <span style={{ color: colors.text.muted, fontWeight: 400 }}>(optional)</span>
                      </label>
                      <textarea
                        style={styles.feedbackBox}
                        value={overallFeedback}
                        disabled={locked}
                        onChange={(e) => setOverallFeedback(e.target.value)}
                        placeholder="Any overall notes or feedback for our team…"
                        rows={4}
                      />
                    </div>

                    <div style={styles.actions}>
                      {!locked && (
                        <>
                          {reviewer === 'Client' ? (
                            <div style={styles.submitActions} className="rca-submit-actions">
                              <button
                                type="button"
                                className="rca-submit-btn rca-btn-primary"
                                style={{ ...styles.button, ...styles.submitButton, ...(submitting ? styles.buttonDisabled : {}) }}
                                disabled={submitting}
                                onClick={() => openConfirm(false)}
                              >
                                {submitting ? 'Submitting…' : 'Approve'}
                              </button>
                              <button
                                type="button"
                                className="rca-submit-btn rca-btn-secondary"
                                style={{
                                  ...styles.buttonSecondary,
                                  ...styles.submitButton,
                                  ...(submitting ? styles.buttonDisabled : {}),
                                }}
                                disabled={submitting}
                                onClick={() => openConfirm(true)}
                              >
                                {submitting ? 'Submitting…' : 'Approve & Auto-Approve Going Forward'}
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="rca-submit-btn rca-btn-primary"
                              style={{ ...styles.button, ...(submitting ? styles.buttonDisabled : {}) }}
                              disabled={submitting}
                              onClick={() => openConfirm(false)}
                            >
                              {submitting ? 'Submitting…' : 'Approve & Send to Client'}
                            </button>
                          )}
                          {reviewer === 'PM' && (
                            <p style={{ marginTop: '10px', fontSize: '13px', color: colors.text.muted, lineHeight: '1.6' }}>
                              Your rejections will be recorded and the approved comments will be forwarded automatically.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={styles.footerText}>
          Visibility Labs &copy; {new Date().getFullYear()}. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default RedditCommentsApproval;
